import { supabase } from '../lib/supabase';

// Get all users from the public.users table
// This table is synced with auth.users via database triggers
export const getAllUsers = async () => {
  try {
    const { data, error } = await supabase
      .from('users')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) throw error;
    
    // Transform the data to match the expected format (similar to auth.users)
    return (data || []).map(user => ({
      id: user.id,
      email: user.email,
      user_metadata: {
        full_name: user.full_name || '',
        role: user.role || 'user',
      },
      email_confirmed_at: user.email_confirmed_at,
      created_at: user.created_at,
      updated_at: user.updated_at,
    }));
  } catch (error) {
    console.error('Error fetching users:', error);
    // Fallback to current user if query fails
    try {
      const { data: { user } } = await supabase.auth.getUser();
      return user ? [user] : [];
    } catch (fallbackError) {
      console.error('Error fetching current user:', fallbackError);
      return [];
    }
  }
};

// Create new user (admin/manager only).
// Uses create-user Edge Function with service role so the caller's session is NOT replaced.
// Uses supabase.functions.invoke() so the client sends apikey + Authorization (required for CORS/gateway).
export const createUser = async (userData) => {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) {
    throw new Error('Not authenticated');
  }

  const { data, error } = await supabase.functions.invoke('create-user', {
    body: {
      email: userData.email,
      password: userData.password,
      full_name: userData.full_name || '',
      role: userData.role || 'tech',
    },
  });

  if (error) {
    // Prefer the server's error message over the generic "non-2xx" message
    const msg = data?.error || data?.details || error.message;
    throw new Error(msg);
  }
  if (data?.error) {
    throw new Error(data.error || data.details || 'Failed to create user');
  }
  return data;
};

// Update user metadata
// Updates both auth.users (via updateUser for current user) and public.users table
// Note: For updating other users' passwords, you'll need a backend endpoint with service role key
export const updateUser = async (userId, userData) => {
  const { full_name, role, password } = userData;
  
  try {
    const { data: { user: currentUser } } = await supabase.auth.getUser();
    if (!currentUser) {
      throw new Error('User not authenticated');
    }

    // If updating the current user, use auth.updateUser (supports password changes)
    if (currentUser.id === userId) {
      const updateData = {
        data: {
          full_name: full_name || '',
          role: role || 'tech',
        },
      };
      
      // Include password if provided
      if (password && password.length >= 6) {
        updateData.password = password;
      }
      
      const { data, error } = await supabase.auth.updateUser(updateData);

      if (error) {
        console.error('Supabase updateUser error:', error);
        throw error;
      }
      
      // Return the updated user data
      return data;
    } else {
      // For other users, update the public.users table directly
      // Note: Password updates for other users require a backend endpoint with service role
      const updateData = {
        full_name: full_name || '',
        role: role || 'tech',
        updated_at: new Date().toISOString(),
      };
      
      const { data, error } = await supabase
        .from('users')
        .update(updateData)
        .eq('id', userId)
        .select()
        .single();

      if (error) {
        console.error('Error updating user in users table:', error);
        throw error;
      }

      // Return in the same format as getAllUsers
      return {
        user: {
          id: data.id,
          email: data.email,
          user_metadata: {
            full_name: data.full_name || '',
            role: data.role || 'user',
          },
          email_confirmed_at: data.email_confirmed_at,
          created_at: data.created_at,
          updated_at: data.updated_at,
        }
      };
    }
  } catch (error) {
    console.error('Error updating user:', error);
    throw error;
  }
};

// Delete a user (admin only). Uses Supabase Edge Function.
export const deleteUser = async (userId) => {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) {
    throw new Error('Not authenticated');
  }
  const { data, error } = await supabase.functions.invoke('delete-user', {
    body: { user_id: userId },
  });
  if (error) throw error;
  if (data?.error) {
    throw new Error(data.error || data.details || 'Failed to delete user');
  }
  return data;
};
